import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, FlaskConical, Beaker, Factory } from "lucide-react";
import { api } from "@/lib/api";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import BenefitIcon from "@/components/BenefitIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import CredentialsStrip from "@/components/CredentialsStrip";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const BENEFITS = [
  {
    kind: "energy",
    eyebrow: "01 / Energy",
    title: "170mg Clean Energy Stack",
    actives: ["Enfinity® Paraxanthine", "Sunphenon® Organic Matcha", "Cordyceps Militaris", "Suntheanine® L-Theanine"],
    body: "for calm, smooth, jitter-free energy & endurance — minus the crash.",
  },
  {
    kind: "mood",
    eyebrow: "02 / Mood",
    title: "Mood & Motivation",
    actives: ["Affron® Saffron", "Phosphatidylserine", "Ashwagandha"],
    body: "for overall brain health, stress resilience, and emotional balance.",
  },
  {
    kind: "cognition",
    eyebrow: "03 / Cognition",
    title: "Cognitive Clarity",
    actives: ["Lion's Mane", "Bacopa Monnieri", "Cognizin® Citicoline", "Rhodiolife® Rhodiola Rosea", "Holy Basil"],
    body: "for mental stamina, fatigue resistance, dialed focus & sharper memory.",
  },
  {
    kind: "recovery",
    eyebrow: "04 / Recovery",
    title: "Stress Resilience & Recovery",
    actives: ["Magnesium Bisglycinate", "UC3 Clear® Curcumin", "Quali®-C Vitamin C"],
    body: "for inflammation, joint & muscle recovery, immunity, and sleep support.",
  },
];

const FAQS = [
  { q: "How much caffeine is in Vitura? Will it make me jittery?",
    a: "Each 2oz shot delivers roughly 170 mg of stimulant load — 150 mg from Enfinity® Paraxanthine (a cleaner caffeine metabolite) plus ~20 mg of natural caffeine from 400 mg of Sunphenon® Green Tea Extract. Paired with 200 mg of Suntheanine® L-Theanine and 300 mg of Ashwagandha, the delivery stays smooth, focused and crash-free — not jittery." },
  { q: "Can I take Vitura every day?",
    a: "Yes — Vitura is formulated for daily use. You'll feel energy and focus in 15–30 minutes, and the long-arc benefits compound over 2–3 weeks." },
  { q: "Is there really zero sugar and zero sucralose?",
    a: "Zero grams of added sugar. Sweetened with monk fruit, a small dose of organic agave, and balanced with citric + malic acid." },
  { q: "Who should not take Vitura?",
    a: "If you're pregnant, nursing, under 18, or on prescription medication, check with your physician first." },
  { q: "How is it tested?",
    a: "Every batch is 3rd-party lab tested by EUROFINS for purity and potency. Manufactured in a US-based cGMP certified facility." },
];

export default function Home() {
  const [data, setData] = useState({ products: [], ingredients: [] });
  const [reviews, setReviews] = useState({ reviews: [], summary: { average: 4.9, count: 50 } });
  const [posts, setPosts] = useState([]);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  useEffect(() => {
    api.products().then(setData).catch(() => {});
    api.reviews().then(setReviews).catch(() => {});
    api.posts().then((r) => setPosts(r.posts)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[color:var(--line)]" data-testid="hero">
        <div className="max-w-site mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 relative z-10">
              <SectionLabel index="01 / Daily Performance">Clinically dosed. Extremely transparent.</SectionLabel>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-[-0.03em] mt-6"
              >
                Engineered for <span className="editorial normal-case">focused minds</span><br />
                & moving bodies.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="mt-6 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]"
              >
                Vitura is the daily performance shot built for high performers chasing their edge — balancing a career, life, and fitness. Smooth energy, calm focus, stress resilience and nightly recovery, all in a 2oz shot of 15 clinically studied ingredients.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <Link to="/preview/shop" className="group inline-flex items-center gap-3 h-12 px-6 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.25em] hover:bg-[#2a2a2a] transition" data-testid="hero-shop-cta">
                  Shop the shot
                  <ArrowUpRight size={14} className="transition group-hover:rotate-45" />
                </Link>
                <Link to="/journal/clinical-doses-matter" className="inline-flex items-center gap-2 h-12 px-6 border border-[color:var(--line)] label !tracking-[0.25em] hover:bg-[color:var(--bg-soft)] transition" data-testid="hero-science-cta">
                  Read the science
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
                className="mt-10"
              >
                <button
                  type="button"
                  onClick={() => setWaitlistOpen(true)}
                  className="group inline-flex items-center gap-3 label hover:opacity-60 transition"
                  data-testid="hero-waitlist-cta"
                >
                  <span className="w-10 h-px bg-[color:var(--ink)]" />
                  <span>Join the early access list</span>
                  <span className="transition group-hover:translate-x-1">→</span>
                </button>
              </motion.div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] bg-[color:var(--bg-soft)] border border-[color:var(--line)] flex items-center justify-center overflow-hidden"
              >
                <video
                  src="/assets/video/hero.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="hero-video"
                >
                  <source src="/assets/video/hero.webm" type="video/webm" />
                  <source src="/assets/video/hero.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(80% 60% at 50% 30%, rgba(168,198,159,0.18) 0%, rgba(244,194,159,0.14) 45%, transparent 75%)" }} />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[#faf9f5] mix-blend-difference">
                  <div>
                    <div className="label !text-[9.5px]">2.0 FL OZ · 60 mL</div>
                    <div className="label !text-[9.5px] mt-1">No. 001 · Daily Performance</div>
                  </div>
                  <div className="label !text-[9.5px] text-right">
                    15 Clinical<br />Actives
                  </div>
                </div>
              </motion.div>
              <div className="absolute -left-4 -top-4 hidden md:block">
                <div className="border border-[color:var(--line)] bg-[color:var(--bg)] p-3 w-40">
                  <div className="label !text-[9.5px] mb-1">Caffeine</div>
                  <div className="font-display text-2xl leading-none">170<span className="text-sm">mg</span></div>
                  <div className="text-[11px] text-[color:var(--ink-3)] mt-1">Paraxanthine + matcha</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee accent items={["15 CLINICALLY STUDIED INGREDIENTS", "0 SUGAR", "0 SUCRALOSE", "3RD PARTY TESTED — EUROFINS", "cGMP MANUFACTURED IN THE USA", "10+ BRANDED ACTIVES"]} />

      {/* FLAVORS */}
      <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="flavors">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <SectionLabel index="02 / Flavors">Two expressions. One formula.</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
              Same clinical stack. <span className="editorial normal-case">Different mornings.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border border-[color:var(--line)]">
          {data.products.map((p, idx) => (
            <Reveal key={p.id} delay={idx * 0.08}>
              <Link to={`/preview/product/${p.handle}`} className="group block relative p-8 md:p-12 border-b md:border-b-0 md:border-r last:border-r-0 last:border-b-0 border-[color:var(--line)] overflow-hidden" data-testid={`flavor-card-${p.handle}`}>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500" style={{ background: `radial-gradient(60% 60% at 50% 40%, ${p.color}55, transparent 70%)` }} />
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className="label !text-[10px]">Flavor {String(idx + 1).padStart(2, "0")}</div>
                    <h3 className="font-display text-3xl md:text-4xl tracking-[-0.02em] mt-2">{p.flavor}</h3>
                    <p className="editorial text-lg text-[color:var(--ink-2)] mt-2">{p.tagline}</p>
                  </div>
                  <span className="w-2.5 h-2.5" style={{ background: p.color }} />
                </div>
                <div className="relative mt-10 aspect-[5/4] flex items-center justify-center">
                  <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${p.color_soft} 0%, transparent 100%)` }} />
                  <img src={p.images[0]} alt={p.flavor} className="relative max-h-[80%] object-contain transition duration-700 group-hover:-translate-y-2" />
                </div>
                <div className="relative mt-6 flex items-center justify-between">
                  <div className="text-sm">From <span className="font-display">${p.variants[0].price.toFixed(0)}</span> · <span className="text-[color:var(--ink-3)]">{p.variants[1].title}</span></div>
                  <span className="label group-hover:opacity-60 transition">Shop →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENEFITS QUADRANT */}
      <section className="border-t border-[color:var(--line)] bg-[color:var(--bg-soft)]" data-testid="benefits">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-4">
              <SectionLabel index="03 / Benefits">One shot. Four systems.</SectionLabel>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
                Not just another energy drink. <br />
                <span className="editorial normal-case">Not just another nootropic shot.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border border-[color:var(--line)] bg-[color:var(--bg)]">
            {BENEFITS.map((b, i) => (
              <Reveal
                key={b.title}
                delay={i * 0.06}
                className={`border-b border-[color:var(--line)] ${i % 2 === 0 ? "md:border-r" : ""} ${i >= BENEFITS.length - 2 ? "md:[&:nth-last-child(-n+2)]:border-b-0" : ""}`}
              >
                <div className="p-8 md:p-12 h-full flex flex-col md:flex-row gap-8 md:gap-10" data-testid={`benefit-${b.kind}`}>
                  <div className="shrink-0 text-[color:var(--ink)]">
                    <BenefitIcon kind={b.kind} className="w-16 h-16 md:w-20 md:h-20" />
                  </div>
                  <div className="flex-1">
                    <div className="label !text-[10px] mb-3">{b.eyebrow}</div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-[-0.02em] leading-[1.1]">{b.title}</h3>
                    <ul className="mt-5 flex flex-wrap gap-x-3 gap-y-2 text-[13px] text-[color:var(--ink-2)]">
                      {b.actives.map((a, idx) => (
                        <li key={a} className="inline-flex items-center gap-2">
                          <span>{a}</span>
                          {idx < b.actives.length - 1 && <span className="text-[color:var(--ink-4)]">+</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="editorial text-lg md:text-xl text-[color:var(--ink)] mt-5 leading-snug">{b.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INGREDIENTS TABLE */}
      <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28" id="ingredients" data-testid="ingredients">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <SectionLabel index="04 / Formula">Extreme label transparency.</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
              Branded actives, <span className="editorial normal-case">verified doses.</span>
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">
              Eight branded actives. Two key minerals broken out on the label. Every batch independently verified by EUROFINS. Below is the full Vitura Performance™ Blend — every active named, in descending order of dose.
            </p>
          </div>
        </div>

        <div className="border border-[color:var(--line)] bg-[color:var(--bg)]">
          <div className="grid grid-cols-12 border-b border-[color:var(--line)] label !text-[10px] py-3 px-4 md:px-6 bg-[color:var(--bg-soft)]">
            <div className="col-span-5">Ingredient</div>
            <div className="col-span-2">Dose</div>
            <div className="col-span-2 hidden md:block">Category</div>
            <div className="col-span-5 md:col-span-3">Benefit</div>
          </div>
          {data.ingredients.map((ing, i) => (
            <div key={ing.name} className="grid grid-cols-12 items-start gap-2 border-b last:border-b-0 border-[color:var(--line)] py-4 px-4 md:px-6 hover:bg-[color:var(--bg-soft)] transition" data-testid={`ingredient-${i}`}>
              <div className="col-span-5 flex items-center gap-2">
                <span className="font-mono tabular-nums text-[10px] text-[color:var(--ink-3)]">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm">{ing.name}</span>
              </div>
              <div className="col-span-2 text-sm">
                {ing.show_dose ? (
                  <span className="inline-block font-mono tabular-nums text-[13px] font-semibold px-2 py-0.5 bg-[#a8c69f]/30 text-[#3a4a32] border border-[#a8c69f]/70">{ing.dose}</span>
                ) : (
                  <span className="inline-flex items-center justify-center w-6 h-6 border border-[color:var(--line)] text-[color:var(--ink-3)]" title="Measured within the Vitura Performance™ Blend">
                    <Beaker size={13} strokeWidth={1.5} />
                  </span>
                )}
              </div>
              <div className="col-span-2 hidden md:block label !text-[10px]">{ing.category}</div>
              <div className="col-span-5 md:col-span-3 text-sm text-[color:var(--ink-2)]">{ing.benefit}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-t border-[color:var(--line)] bg-[#0f0f10] text-[#faf9f5]" data-testid="trust">
        <div className="max-w-site mx-auto px-6 md:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: FlaskConical, label: "3rd Party Lab Tested", sub: "Eurofins, every batch" },
            { icon: Factory, label: "cGMP US Facility", sub: "Certified manufacturing" },
            { icon: Beaker, label: "0 Sugar · 0 Sucralose", sub: "Clean sweeteners only" },
            { icon: ShieldCheck, label: "15 Clinical Actives", sub: "Dosed for efficacy" },
          ].map((t, i) => (
            <Reveal key={t.label} delay={i * 0.05}>
              <div className="flex flex-col gap-3" data-testid={`trust-${i}`}>
                <t.icon size={24} strokeWidth={1.2} />
                <div>
                  <div className="font-display text-lg tracking-[-0.01em]">{t.label}</div>
                  <div className="label !text-[10px] !text-[#faf9f5]/60 mt-1">{t.sub}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CREDENTIALS — facility certifications */}
      <CredentialsStrip tone="dark" />

      {/* EDITORIAL / SCIENCE SPLIT */}
      <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="science">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionLabel index="05 / The Thesis">Why Vitura exists.</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] mt-6">
              Most products give you <span className="editorial normal-case">energy</span>.<br />
              A few help you <span className="editorial normal-case">focus</span>.<br />
              Almost none deliver all four — <span className="editorial normal-case">without the crash</span>.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[color:var(--ink-2)] max-w-xl">
              We engineered Vitura by combining clinically-backed nootropics, adaptogens, and functional botanicals that work with each other. Clean energy, sharp mental clarity, mood balance, stress resilience, and nightly recovery — in a single daily shot.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/story" className="label underline" data-testid="science-story-link">Read our story →</Link>
              <Link to="/journal" className="label underline" data-testid="science-journal-link">Journal →</Link>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/5] border border-[color:var(--line)] bg-[color:var(--bg-soft)] overflow-hidden">
              <img src="https://static.prod-images.emergentagent.com/jobs/f6de307f-7947-469c-b742-e406e32e2f15/images/16a87531941a10e6374e6653578330b3b3109b33aa3fadfe0f64e716474a26d0.png" alt="Ingredient macro" className="w-full h-full object-cover mix-blend-multiply" />
              <div className="absolute top-4 left-4 right-4 flex justify-between label !text-[10px] text-[#0f0f10]">
                <span>Fig. 01</span><span>Ingredient Panel</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="border-t border-[color:var(--line)] bg-[color:var(--bg-soft)]" data-testid="reviews">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <SectionLabel index="06 / Proof">Data, not vibes.</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] mt-6">
                <span className="editorial normal-case">They felt it.</span> Their wearables confirmed it.
              </h2>
            </div>
            <div className="text-right">
              <div className="font-display text-5xl leading-none">{reviews.summary.average}<span className="text-lg text-[color:var(--ink-3)]">/5</span></div>
              <div className="label !text-[10px] mt-1">{reviews.summary.count.toLocaleString()} beta tester reviews</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-[color:var(--line)] bg-[color:var(--bg)]">
            {reviews.reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.05} className="border-b lg:border-b-0 md:even:border-l lg:even:border-l-0 lg:[&:nth-child(3n+2)]:border-x border-[color:var(--line)]">
                <div className="p-8 h-full flex flex-col" data-testid={`review-${i}`}>
                  <div className="flex items-center gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, s) => (<span key={s} className="text-[10px]">★</span>))}
                  </div>
                  <p className="editorial text-xl leading-snug text-[color:var(--ink)]">&ldquo;{r.body}&rdquo;</p>
                  <div className="mt-auto pt-8">
                    <div className="text-sm">{r.name}</div>
                    <div className="label !text-[10px] mt-1">{r.role} · {r.location}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="journal-preview">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <SectionLabel index="07 / Journal">Letters from the lab.</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] mt-6">
              Longform, <span className="editorial normal-case">on what actually works.</span>
            </h2>
          </div>
          <Link to="/journal" className="label underline" data-testid="journal-all">Read all →</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.05}>
              <Link to={`/journal/${p.slug}`} className="group block border border-[color:var(--line)] bg-[color:var(--bg)] hover:bg-[color:var(--bg-soft)] transition" data-testid={`journal-card-${i}`}>
                <div className="aspect-[4/3] bg-[color:var(--bg-soft)] overflow-hidden">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.02]" />
                </div>
                <div className="p-6">
                  <div className="label !text-[10px]">{p.category} · {p.read_time}</div>
                  <h3 className="font-display text-xl mt-3 tracking-[-0.01em]">{p.title}</h3>
                  <p className="text-sm text-[color:var(--ink-2)] mt-2">{p.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[color:var(--line)]" id="faq" data-testid="faq">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <SectionLabel index="08 / FAQ">Frequently asked.</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] mt-6">
                The answers <span className="editorial normal-case">before you ask.</span>
              </h2>
            </div>
            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full">
                {FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`f-${i}`} className="border-b border-[color:var(--line)]" data-testid={`faq-${i}`}>
                    <AccordionTrigger className="py-6 text-left font-display text-lg md:text-xl tracking-[-0.01em] hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="pb-6 text-[15px] text-[color:var(--ink-2)] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f0f10] text-[#faf9f5]" data-testid="final-cta">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="label !text-[10px] !text-[#faf9f5]/60">09 / Ready</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-[-0.03em] leading-[0.95] mt-4">
            Be the first to try <span className="editorial normal-case">Vitura</span>.
          </h2>
          <p className="mt-5 max-w-lg mx-auto text-[15px] text-[#faf9f5]/70">
            Reserve your spot. Early access pricing. Shipping in batches.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/preview/shop" className="inline-flex items-center gap-2 h-12 px-6 bg-[#faf9f5] text-[#0f0f10] label !tracking-[0.25em] hover:bg-white transition" data-testid="final-cta-shop">
              Shop the shot →
            </Link>
            <button
              type="button"
              onClick={() => setWaitlistOpen(true)}
              className="inline-flex items-center gap-2 h-12 px-6 border border-[#faf9f5]/30 label !tracking-[0.25em] hover:bg-[#faf9f5]/10 transition"
              data-testid="final-cta-waitlist"
            >
              Join the early access list
            </button>
          </div>
        </div>
      </section>

      <EarlyAccessModal open={waitlistOpen} onOpenChange={setWaitlistOpen} source="home" />
    </div>
  );
}
